import { useState, useEffect } from 'react'
import { Database, Play, RefreshCw, Download, Info, Zap, HardDrive, Table, FileText } from 'lucide-react'
import api from '../services/api'

const PostgresExplorer = () => {
  const [stats, setStats] = useState(null)
  const [tables, setTables] = useState([])
  const [selectedTable, setSelectedTable] = useState(null)
  const [tableSchema, setTableSchema] = useState(null)
  const [sampleQueries, setSampleQueries] = useState([])
  const [queryInput, setQueryInput] = useState('')
  const [queryResults, setQueryResults] = useState(null)
  const [loading, setLoading] = useState(true)
  const [executing, setExecuting] = useState(false)
  const [activeTab, setActiveTab] = useState('query') // 'query', 'tables', 'samples'

  useEffect(() => {
    loadInitialData()
  }, [])

  const loadInitialData = async () => {
    try {
      await Promise.all([
        loadStats(),
        loadTables(),
        loadSampleQueries()
      ])
    } catch (error) {
      console.error('Error loading initial data:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const data = await api.get('/postgres/stats')
      setStats(data)
    } catch (error) {
      console.error('Error loading stats:', error)
    }
  }

  const loadTables = async () => {
    try {
      const data = await api.get('/postgres/tables')
      setTables(data.tables || [])
    } catch (error) {
      console.error('Error loading tables:', error)
    }
  }

  const loadSampleQueries = async () => {
    try {
      const data = await api.get('/postgres/sample-queries')
      setSampleQueries(data.queries || [])
    } catch (error) {
      console.error('Error loading sample queries:', error)
    }
  }

  const loadTableSchema = async (schema, tableName) => {
    try {
      const data = await api.get(`/postgres/tables/${schema}/${tableName}/schema`)
      setTableSchema(data)
      setSelectedTable(tableName)
    } catch (error) {
      console.error('Error loading table schema:', error)
      alert(`Failed to load schema: ${error.message}`)
    }
  }

  const executeQuery = async () => {
    if (!queryInput.trim()) {
      alert('Please enter a query')
      return
    }

    setExecuting(true)
    try {
      const data = await api.post('/postgres/query', {
        query: queryInput,
        limit: 1000
      })
      setQueryResults(data)
    } catch (error) {
      alert(`Query failed: ${error.response?.data?.detail || error.message}`)
      setQueryResults(null)
    } finally {
      setExecuting(false)
    }
  }

  const loadSampleQuery = (sql) => {
    setQueryInput(sql)
    setActiveTab('query')
  }

  const exportToCSV = () => {
    if (!queryResults || !queryResults.rows.length) {
      alert('No data to export')
      return
    }

    const csv = [
      queryResults.columns.join(','),
      ...queryResults.rows.map(row => row.map(cell =>
        typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
      ).join(','))
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `postgres_query_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Database className="w-8 h-8 text-purple-600" />
          <h1 className="text-3xl font-bold text-gray-900">PostgreSQL Database Explorer</h1>
        </div>
        <p className="text-gray-600">Query and explore the PostgreSQL production database</p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-600">Database</div>
              <Database className="w-5 h-5 text-purple-500" />
            </div>
            <div className="text-2xl font-bold text-gray-900">{stats.database_name}</div>
            <div className="text-xs text-gray-500 mt-1">{stats.connection_count} connections</div>
          </div>

          <div className="bg-white rounded-lg shadow p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-600">Database Size</div>
              <HardDrive className="w-5 h-5 text-orange-500" />
            </div>
            <div className="text-2xl font-bold text-gray-900">{stats.database_size}</div>
            <div className="text-xs text-gray-500 mt-1">On disk</div>
          </div>

          <div className="bg-white rounded-lg shadow p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-600">Tables</div>
              <Table className="w-5 h-5 text-blue-500" />
            </div>
            <div className="text-2xl font-bold text-gray-900">{stats.total_tables}</div>
            <div className="text-xs text-gray-500 mt-1">In public schema</div>
          </div>

          <div className="bg-white rounded-lg shadow p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-600">Total Rows</div>
              <FileText className="w-5 h-5 text-green-500" />
            </div>
            <div className="text-2xl font-bold text-gray-900">{stats.total_rows.toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-1">Estimated</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200 flex">
          <button
            onClick={() => setActiveTab('query')}
            className={`px-6 py-3 font-medium ${activeTab === 'query' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-gray-600 hover:text-gray-900'}`}
          >
            SQL Query
          </button>
          <button
            onClick={() => setActiveTab('tables')}
            className={`px-6 py-3 font-medium ${activeTab === 'tables' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-gray-600 hover:text-gray-900'}`}
          >
            Tables & Schema
          </button>
          <button
            onClick={() => setActiveTab('samples')}
            className={`px-6 py-3 font-medium ${activeTab === 'samples' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-gray-600 hover:text-gray-900'}`}
          >
            Sample Queries
          </button>
        </div>

        <div className="p-6">
          {/* SQL Query Tab */}
          {activeTab === 'query' && (
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">SQL Query (read-only)</label>
                <textarea
                  value={queryInput}
                  onChange={(e) => setQueryInput(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg font-mono text-sm"
                  rows="6"
                  placeholder="SELECT * FROM bars LIMIT 100"
                />
                <div className="text-xs text-gray-500 mt-1">
                  Only SELECT queries allowed. Results limited to 1,000 rows.
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={executeQuery}
                  disabled={executing || !queryInput.trim()}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {executing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Executing...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      Execute Query
                    </>
                  )}
                </button>

                {queryResults && (
                  <button
                    onClick={exportToCSV}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export CSV
                  </button>
                )}

                <button
                  onClick={() => { setQueryInput(''); setQueryResults(null); }}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Clear
                </button>
              </div>

              {/* Query Results */}
              {queryResults && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="text-sm font-medium text-gray-700">
                        Results: {queryResults.row_count} rows
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <Zap className="w-4 h-4" />
                        {queryResults.execution_time_ms}ms
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-lg overflow-auto max-h-96">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          {queryResults.columns.map((col, idx) => (
                            <th key={idx} className="px-4 py-2 text-left font-medium text-gray-700 border-b">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {queryResults.rows.map((row, rowIdx) => (
                          <tr key={rowIdx} className="hover:bg-gray-50 border-b">
                            {row.map((cell, cellIdx) => (
                              <td key={cellIdx} className="px-4 py-2 text-gray-900">
                                {cell === null ? <span className="text-gray-400 italic">NULL</span> : String(cell)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tables & Schema Tab */}
          {activeTab === 'tables' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Tables List */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Database Tables</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {tables.map((table) => (
                    <div
                      key={table.name}
                      onClick={() => loadTableSchema(table.schema, table.name)}
                      className={`border rounded-lg p-4 cursor-pointer hover:bg-gray-50 ${selectedTable === table.name ? 'border-purple-600 bg-purple-50' : ''}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold text-gray-900">{table.name}</div>
                        <Table className="w-4 h-4 text-gray-500" />
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs text-gray-600">
                        <div>
                          <span className="font-medium">Rows:</span> {table.row_count.toLocaleString()}
                        </div>
                        <div>
                          <span className="font-medium">Size:</span> {table.size}
                        </div>
                        <div>
                          <span className="font-medium">Schema:</span> {table.schema}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Schema Details */}
              <div>
                {tableSchema ? (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      Schema: {tableSchema.table_name}
                    </h3>

                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Columns</h4>
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">Name</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">Type</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-700">Null</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {tableSchema.columns.map((col, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-3 py-2 font-mono text-xs">{col.name}</td>
                                <td className="px-3 py-2 text-gray-700 text-xs">{col.type}</td>
                                <td className="px-3 py-2 text-xs">
                                  <span className={`px-2 py-1 rounded text-xs ${col.nullable === 'YES' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                                    {col.nullable}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {tableSchema.indexes && tableSchema.indexes.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Indexes</h4>
                        <div className="space-y-2">
                          {tableSchema.indexes.map((idx, i) => (
                            <div key={i} className="bg-gray-50 rounded p-3 text-xs">
                              <div className="font-medium text-gray-900 mb-1">{idx.name}</div>
                              <div className="text-gray-600 font-mono text-xs">{idx.definition}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    <div className="text-center">
                      <Info className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      <p>Select a table to view its schema</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sample Queries Tab */}
          {activeTab === 'samples' && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 mb-4">
                Click on a sample query to load it into the query editor
              </div>
              {sampleQueries.map((sample, idx) => (
                <div key={idx} className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer" onClick={() => loadSampleQuery(sample.sql)}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-semibold text-gray-900">{sample.name}</div>
                      <div className="text-sm text-gray-600 mt-1">{sample.description}</div>
                    </div>
                    <Play className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  </div>
                  <div className="mt-3 bg-gray-50 rounded p-3 text-xs font-mono text-gray-700 overflow-x-auto">
                    {sample.sql}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PostgresExplorer
